"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

interface PriceRangeChartProps {
  currentPrice: number;
  minPrice: string;
  maxPrice: string;
  onMinChange: (value: string) => void;
  onMaxChange: (value: string) => void;
  quoteSymbol?: string;
}

function formatPrice(v: number) {
  if (!Number.isFinite(v) || v <= 0) return "0";
  const trimmed = v.toFixed(6).replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
  return trimmed;
}

// Draggable dual-handle price range slider. Domain is a fixed 0..4x band
// around the current pool price (or 1 as a fallback pre-pool) rather than
// real on-chain tick-liquidity depth — see useV3LiquidityDistribution for
// the real data source if that's ever wired up here.
export function PriceRangeChart({ currentPrice, minPrice, maxPrice, onMinChange, onMaxChange, quoteSymbol }: PriceRangeChartProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<"min" | "max" | null>(null);
  const [hovered, setHovered] = useState<"min" | "max" | null>(null);

  // Domain is seeded once (from the pool price, or the initially-typed
  // min/max, or 1) and then only follows currentPrice — never minPrice/
  // maxPrice — after that. Deriving it from the live dragged value would
  // make it self-referential: with domainHi = minPrice * 4, minFrac would
  // always resolve to exactly 0.25 no matter where the handle is dragged.
  const [seededBase, setSeededBase] = useState(
    () => currentPrice > 0 ? currentPrice : Number(minPrice) || Number(maxPrice) || 1
  );
  useEffect(() => {
    if (currentPrice > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSeededBase(currentPrice);
    }
  }, [currentPrice]);
  const base = seededBase;
  const domainLo = 0;
  const domainHi = base * 4;
  const step = (domainHi - domainLo) * 0.01;

  const parsedMin = Math.max(domainLo, Math.min(Number(minPrice) || 0, domainHi));
  const parsedMax = maxPrice ? Math.max(domainLo, Math.min(Number(maxPrice), domainHi)) : domainHi;

  const toFrac = (v: number) => (domainHi === domainLo ? 0 : (v - domainLo) / (domainHi - domainLo));
  const toPrice = (frac: number) => domainLo + Math.max(0, Math.min(1, frac)) * (domainHi - domainLo);

  const minFrac = toFrac(parsedMin);
  const maxFrac = toFrac(parsedMax);
  const currentFrac = toFrac(Math.max(domainLo, Math.min(currentPrice, domainHi)));
  const inRange = currentPrice > 0 && currentPrice >= parsedMin && currentPrice <= parsedMax;

  const updateFromClientX = useCallback(
    (clientX: number, handle: "min" | "max") => {
      const track = trackRef.current;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      const price = toPrice((clientX - rect.left) / rect.width);
      if (handle === "min") {
        onMinChange(formatPrice(Math.max(0, Math.min(price, parsedMax - step))));
      } else {
        onMaxChange(formatPrice(Math.max(price, parsedMin + step)));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [domainHi, domainLo, parsedMax, parsedMin, step, onMinChange, onMaxChange]
  );

  function startDrag(handle: "min" | "max") {
    return (e: React.PointerEvent) => {
      e.preventDefault();
      setDragging(handle);
      const move = (ev: PointerEvent) => updateFromClientX(ev.clientX, handle);
      const up = () => {
        setDragging(null);
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
    };
  }

  function nudge(handle: "min" | "max", direction: 1 | -1) {
    if (handle === "min") {
      onMinChange(formatPrice(Math.max(0, Math.min(parsedMin + direction * step, parsedMax - step))));
    } else {
      onMaxChange(formatPrice(Math.max(parsedMax + direction * step, parsedMin + step)));
    }
  }

  function handleKeyDown(handle: "min" | "max") {
    return (e: React.KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === "ArrowUp") {
        e.preventDefault();
        nudge(handle, 1);
      } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
        e.preventDefault();
        nudge(handle, -1);
      }
    };
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">
          {currentPrice > 0 ? `Current price: ${formatPrice(currentPrice)} ${quoteSymbol ?? ""}` : "Drag to set your price range"}
        </span>
        {currentPrice > 0 && (
          <span className={inRange ? "text-[color:var(--color-in-range)]" : "text-[color:var(--color-out-range)]"}>
            {inRange ? "● In range" : "● Out of range"}
          </span>
        )}
      </div>
      <div ref={trackRef} className="relative h-14 touch-none rounded-lg bg-muted select-none">
        <div
          className="absolute inset-y-0 rounded-lg bg-primary/20"
          style={{ left: `${minFrac * 100}%`, width: `${Math.max(0, maxFrac - minFrac) * 100}%` }}
        />
        {currentPrice > 0 && (
          <div
            className="absolute inset-y-0 w-px border-l border-dashed border-foreground/40"
            style={{ left: `${currentFrac * 100}%` }}
          />
        )}
        <div
          role="slider"
          aria-label="Minimum price"
          aria-valuenow={parsedMin}
          tabIndex={0}
          onPointerDown={startDrag("min")}
          onPointerEnter={() => setHovered("min")}
          onPointerLeave={() => setHovered(null)}
          onKeyDown={handleKeyDown("min")}
          className={cn(
            "absolute top-1/2 z-10 size-5 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize rounded-full border-2 border-primary bg-background shadow focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50",
            dragging === "min" && "ring-3 ring-ring/50"
          )}
          style={{ left: `${minFrac * 100}%` }}
        >
          {(dragging === "min" || hovered === "min") && (
            <div className="pointer-events-none absolute bottom-full left-1/2 mb-1.5 -translate-x-1/2 rounded-md bg-foreground px-2 py-1 text-xs font-medium whitespace-nowrap text-background shadow">
              Min: {formatPrice(parsedMin)} {quoteSymbol ?? ""}
            </div>
          )}
        </div>
        <div
          role="slider"
          aria-label="Maximum price"
          aria-valuenow={parsedMax}
          tabIndex={0}
          onPointerDown={startDrag("max")}
          onPointerEnter={() => setHovered("max")}
          onPointerLeave={() => setHovered(null)}
          onKeyDown={handleKeyDown("max")}
          className={cn(
            "absolute top-1/2 z-10 size-5 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize rounded-full border-2 border-primary bg-background shadow focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50",
            dragging === "max" && "ring-3 ring-ring/50"
          )}
          style={{ left: `${maxFrac * 100}%` }}
        >
          {(dragging === "max" || hovered === "max") && (
            <div className="pointer-events-none absolute bottom-full left-1/2 mb-1.5 -translate-x-1/2 rounded-md bg-foreground px-2 py-1 text-xs font-medium whitespace-nowrap text-background shadow">
              Max: {maxPrice ? formatPrice(parsedMax) : "∞"} {quoteSymbol ?? ""}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
